import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  TextField,
  Box,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Laptop, CheckCircle } from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { API_URL } from '../../globals';
import axios from 'axios';

const ConfirmationDialog = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#121212',
          backgroundImage: 'none',
          borderRadius: 1,
          border: '1px solid #2a2a2a',
        }
      }}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 4,
        textAlign: 'center',
        gap: 2
      }}>
        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50' }} />
        <Typography sx={{ color: '#fff', fontSize: '1.25rem', fontWeight: 600 }}>
          You are all set!
        </Typography>
        <Typography sx={{ color: '#ccc', fontSize: '0.875rem', lineHeight: 1.5 }}>
          We will send you an email when the exploration is done
        </Typography>
        <Button
          onClick={onClose}
          sx={{
            mt: 2,
            border: '1px solid #333',
            backgroundColor: 'transparent',
            color: '#fff',
            px: 3,
            '&:hover': {
              backgroundColor: '#252525',
            }
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

const PosthogTestModal = ({ open, onClose, studioId }) => {
  const [appUrl, setAppUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleCreate = async () => {
    setIsProcessing(true);
    setError(null);
    
    try {
      const session = await supabase.auth.getSession();
      if (!session?.data?.session?.access_token) {
        throw new Error("Not authenticated");
      }
      
      const authToken = session.data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      };

      // First, save the default values
      const defaultPayload = {
        studio_id: studioId,
        app_url: appUrl.trim(),
        notes: notes.trim()
      };

      await axios.post(
        `${API_URL}/studio/posthog-mapping/default`,
        defaultPayload,
        { headers }
      );

      // Then create the test group
      const testPayload = {
        studio_id: studioId,
        app_url: appUrl.trim(),
        notes: notes.trim()
      };

      const response = await axios.post(
        `${API_URL}/test_e2e`,
        testPayload,
        { headers }
      );

      if (response.status === 200) {
        // Show confirmation dialog
        setShowConfirmation(true);
      } else {
        throw new Error(response.data?.error || 'Failed to create test group');
      }
    } catch (error) {
      console.error('Error:', error);
      setError(error.message || 'Failed to save default values or create test group');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setAppUrl('');
    setNotes('');
    setError(null);
    onClose();
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#121212',
            backgroundImage: 'none',
            borderRadius: 1,
            border: '1px solid #2a2a2a',
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          borderBottom: '1px solid #2a2a2a'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Laptop style={{ color: '#fff' }} />
            <Box>
              <Typography sx={{ color: '#fff', fontSize: '1.25rem', fontWeight: 600, mb: 0.5 }}>
                Exploration
              </Typography>
              <Typography sx={{ color: '#ccc', fontSize: '0.875rem' }}>
                Fume will explore your application after watching the session recordings
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: '#ccc', '&:hover': { color: '#fff', backgroundColor: '#252525' }}}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography sx={{ color: '#ccc', fontSize: '0.875rem', lineHeight: 1.5 }}>
              Can you provide the staging environment URL and login information (may be one or multiple accounts)?
            </Typography>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#ccc' }}>
                  Staging URL
                </Typography>
              </Box>
              <TextField
                fullWidth
                placeholder="Enter the staging environment URL"
                value={appUrl}
                onChange={(e) => setAppUrl(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#121212',
                    border: '1px solid #333',
                    borderRadius: 1,
                    color: '#fff',
                    '&:hover': {
                      borderColor: '#666',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#ccc',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#999',
                    opacity: 1
                  }
                }}
              />
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#ccc' }}>
                  Login Information
                </Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Add login credentials for one or multiple accounts"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#121212',
                    border: '1px solid #333',
                    borderRadius: 1,
                    color: '#fff',
                    '&:hover': {
                      borderColor: '#666',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#ccc',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#999',
                    opacity: 1
                  }
                }}
              />
            </Box>

            <Typography sx={{ color: '#ccc', fontSize: '0.875rem', lineHeight: 1.5 }}>
              Staging behind VPN?{' '}
              <a 
                href="mailto:contact@fumedev.com" 
                style={{ 
                  color: '#fff', 
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Contact us
              </a>
            </Typography>

            {error && (
              <Box sx={{ 
                p: 2,
                bgcolor: 'rgba(211, 47, 47, 0.1)',
                borderRadius: 1,
                border: '1px solid rgba(211, 47, 47, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Typography variant="body2" sx={{ color: '#ff6b6b' }}>
                  {error}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ 
            p: 3, 
            borderTop: '1px solid #2a2a2a',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2
          }}>
            <Button
              onClick={handleClose}
              sx={{
                color: '#888',
                '&:hover': {
                  backgroundColor: '#252525',
                  color: '#fff'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={isProcessing || !appUrl.trim()}
              sx={{
                border: '1px solid #333',
                backgroundColor: 'transparent',
                color: appUrl.trim() ? '#fff' : '#666',
                px: 3,
                '&:hover': {
                  backgroundColor: '#252525',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #2a2a2a',
                }
              }}
            >
              {isProcessing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Start Exploration'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog 
        open={showConfirmation}
        onClose={handleConfirmationClose}
      />
    </>
  );
};

export default PosthogTestModal; 